import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Chip,
  InputAdornment,
  FormControlLabel,
  Switch,
  Autocomplete,
} from '@mui/material';
import { AttachMoney, Schedule } from '@mui/icons-material';

interface ServiceFormData {
  name: string;
  description: string;
  category: string;
  subcategory: string;
  duration: number;
  price: number;
  discountPrice?: number;
  isActive: boolean;
  requiredSkills: string[];
}

interface ServiceFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: ServiceFormData) => void;
  initialData?: Partial<ServiceFormData>;
  mode: 'create' | 'edit';
}

const categories = [
  'Makeup',
  'Hair',
  'Skincare',
  'Nails',
  'Massage',
  'Threading',
  'Waxing',
  'Eyebrows',
  'Eyelashes',
];

const subcategoriesByCategory: Record<string, string[]> = {
  Makeup: ['Bridal', 'Party', 'Casual', 'HD Makeup', 'Airbrush'],
  Hair: ['Styling', 'Cut', 'Color', 'Treatment', 'Bridal', 'Wash & Blow Dry'],
  Skincare: ['Facial', 'Cleanup', 'Anti-Aging', 'Acne Treatment', 'Hydrating'],
  Nails: ['Manicure', 'Pedicure', 'Nail Art', 'Gel Polish', 'Nail Extension'],
  Massage: ['Relaxing', 'Deep Tissue', 'Head Massage', 'Foot Massage'],
  Threading: ['Eyebrows', 'Upper Lip', 'Full Face', 'Chin'],
  Waxing: ['Full Body', 'Arms', 'Legs', 'Face', 'Bikini'],
  Eyebrows: ['Threading', 'Tinting', 'Shaping', 'Microblading'],
  Eyelashes: ['Extension', 'Tinting', 'Perming', 'Lifting'],
};

const allSkills = [
  'Makeup',
  'Bridal Makeup',
  'Hair Styling',
  'Hair Cutting',
  'Hair Coloring',
  'Skincare',
  'Facial',
  'Nail Art',
  'Nail Care',
  'Massage Therapy',
  'Threading',
  'Waxing',
  'Eyebrow Shaping',
  'Eyelash Extension',
  'Customer Service',
  'Product Knowledge',
];

const ServiceForm: React.FC<ServiceFormProps> = ({
  open,
  onClose,
  onSubmit,
  initialData,
  mode,
}) => {
  const [formData, setFormData] = useState<ServiceFormData>({
    name: '',
    description: '',
    category: '',
    subcategory: '',
    duration: 60,
    price: 0,
    discountPrice: undefined,
    isActive: true,
    requiredSkills: [],
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name || '',
        description: initialData.description || '',
        category: initialData.category || '',
        subcategory: initialData.subcategory || '',
        duration: initialData.duration || 60,
        price: initialData.price || 0,
        discountPrice: initialData.discountPrice,
        isActive: initialData.isActive !== undefined ? initialData.isActive : true,
        requiredSkills: initialData.requiredSkills || [],
      });
    } else {
      setFormData({
        name: '',
        description: '',
        category: '',
        subcategory: '',
        duration: 60,
        price: 0,
        discountPrice: undefined,
        isActive: true,
        requiredSkills: [],
      });
    }
  }, [initialData, open]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Service name is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (!formData.category) {
      newErrors.category = 'Category is required';
    }

    if (!formData.subcategory) {
      newErrors.subcategory = 'Subcategory is required';
    }

    if (formData.duration <= 0) {
      newErrors.duration = 'Duration must be greater than 0';
    }

    if (formData.price <= 0) {
      newErrors.price = 'Price must be greater than 0';
    }

    if (formData.discountPrice && formData.discountPrice >= formData.price) {
      newErrors.discountPrice = 'Discount price must be less than regular price';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onSubmit(formData);
      onClose();
    }
  };

  const handleCategoryChange = (category: string) => {
    setFormData(prev => ({
      ...prev,
      category,
      subcategory: '', // Reset subcategory when category changes
    }));
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const getDiscountPercentage = () => {
    if (formData.discountPrice && formData.price > 0) {
      return Math.round(((formData.price - formData.discountPrice) / formData.price) * 100);
    }
    return 0;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {mode === 'create' ? 'Create New Service' : 'Edit Service'}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={3} sx={{ mt: 1 }}>
          {/* Basic Information */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Basic Information
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Service Name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              error={!!errors.name}
              helperText={errors.name}
              required
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                />
              }
              label="Active Service"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              multiline
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              error={!!errors.description}
              helperText={errors.description}
              required
            />
          </Grid>

          {/* Category & Subcategory */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Category & Classification
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth error={!!errors.category}>
              <InputLabel>Category</InputLabel>
              <Select
                value={formData.category}
                label="Category"
                onChange={(e) => handleCategoryChange(e.target.value)}
                required
              >
                {categories.map((category) => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                ))}
              </Select>
              {errors.category && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                  {errors.category}
                </Typography>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth error={!!errors.subcategory}>
              <InputLabel>Subcategory</InputLabel>
              <Select
                value={formData.subcategory}
                label="Subcategory"
                onChange={(e) => setFormData(prev => ({ ...prev, subcategory: e.target.value }))}
                disabled={!formData.category}
                required
              >
                {formData.category && subcategoriesByCategory[formData.category]?.map((subcategory) => (
                  <MenuItem key={subcategory} value={subcategory}>
                    {subcategory}
                  </MenuItem>
                ))}
              </Select>
              {errors.subcategory && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                  {errors.subcategory}
                </Typography>
              )}
            </FormControl>
          </Grid>

          {/* Duration & Pricing */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Duration & Pricing
            </Typography>
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Duration (minutes)"
              type="number"
              value={formData.duration}
              onChange={(e) => setFormData(prev => ({ ...prev, duration: parseInt(e.target.value) || 0 }))}
              error={!!errors.duration}
              helperText={errors.duration}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Schedule />
                  </InputAdornment>
                ),
              }}
              required
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Regular Price"
              type="number"
              value={formData.price}
              onChange={(e) => setFormData(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
              error={!!errors.price}
              helperText={errors.price}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AttachMoney />
                  </InputAdornment>
                ),
              }}
              required
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Discount Price (Optional)"
              type="number"
              value={formData.discountPrice || ''}
              onChange={(e) => setFormData(prev => ({ 
                ...prev, 
                discountPrice: e.target.value ? parseFloat(e.target.value) : undefined 
              }))}
              error={!!errors.discountPrice}
              helperText={errors.discountPrice}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AttachMoney />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {/* Pricing Summary */}
          {formData.price > 0 && (
            <Grid item xs={12}>
              <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Pricing Summary
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Regular Price:
                    </Typography>
                    <Typography variant="h6">
                      {formatCurrency(formData.price)}
                    </Typography>
                  </Grid>
                  {formData.discountPrice && (
                    <>
                      <Grid item xs={6} md={3}>
                        <Typography variant="body2" color="text.secondary">
                          Discount Price:
                        </Typography>
                        <Typography variant="h6" color="primary">
                          {formatCurrency(formData.discountPrice)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="body2" color="text.secondary">
                          You Save:
                        </Typography>
                        <Typography variant="h6" color="success.main">
                          {formatCurrency(formData.price - formData.discountPrice)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="body2" color="text.secondary">
                          Discount:
                        </Typography>
                        <Typography variant="h6">
                          {getDiscountPercentage()}%
                        </Typography>
                      </Grid>
                    </>
                  )}
                </Grid>
              </Box>
            </Grid>
          )}

          {/* Required Skills */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Required Skills
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Autocomplete
              multiple
              options={allSkills}
              value={formData.requiredSkills}
              onChange={(_, newValue) => setFormData(prev => ({ ...prev, requiredSkills: newValue }))}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    label={option}
                    {...getTagProps({ index })}
                    key={option}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Required Skills"
                  placeholder="Select skills required for this service"
                  helperText="Select the skills that staff members need to perform this service"
                />
              )}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit}>
          {mode === 'create' ? 'Create Service' : 'Update Service'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ServiceForm;